import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { Button, Grid, Link as MuiLink, MenuItem, Paper, Stack, Typography } from '@mui/material';
import {
  UpdateRoleDTO,
  useGetAuthoritiesQuery,
  useGetRoleByIdQuery,
  useUpdateRoleMutation,
} from '../../../api/controllers/admin-roles';
import { permissionsCheck } from '../../../utils/permission';
import { RouterPaths } from '../../../router/paths';
import { InfoField, InfoFieldChangeHandler } from '../../../components';

export const RoleDetails: FC = () => {
  const { t } = useTranslation();
  const { roleId } = useParams<'roleId'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditRole = permissionsCheck(['WRITE_ROLES']);

  // api
  const {
    data: role,
    isLoading: isRoleLoading,
    refetch,
  } = useGetRoleByIdQuery({
    roleId: Number(roleId),
  });
  const [updateRole, { isLoading: isUpdateRoleLoading }] = useUpdateRoleMutation();
  const { data: authorities } = useGetAuthoritiesQuery();

  // variables
  const isLoading = isRoleLoading || isUpdateRoleLoading;

  // fields control logic
  const [fields, setFields] = useState<Partial<UpdateRoleDTO['body']> | null>(null);

  const fieldsHandler: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // validation for required fields
  const requiredFields = ['authorities', 'name'] as (keyof UpdateRoleDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    return false;
  };

  // edit mode logic
  const [isEditMode, setEditMode] = useState(false);
  const openEditMode = () => {
    setFields({
      authorities: role?.role.authorities.map((it) => it.name),
      comment: role?.role.comment,
      name: role?.role.name,
    });
    setEditMode(true);
  };
  const closeEditMode = () => {
    setEditMode(false);
    setFields(null);
  };

  // edit handler
  const onUpdateRole = async () => {
    if (!fields || !roleId) return;

    const args = {
      roleId: Number(roleId),
      body: {
        comment: fields.comment,
        name: fields.name,
        authorities: fields.authorities,
      },
    };

    try {
      // @ts-ignore
      await updateRole(args).unwrap();
      closeEditMode();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // elements
  const authoritiesSelectOptions = authorities?.authorities.map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {it}
      </MenuItem>
    );
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} pl={3} pt={1} width="100%">
        <MuiLink underline="hover" component={Link} to={RouterPaths.Roles}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('users-and-roles.roles.details.back-to-roles')}
            </Typography>
          </Stack>
        </MuiLink>

        <Stack direction="row" justifyContent="flex-end" spacing={2} flexGrow={1}>
          {!isEditMode && hasPermissionToEditRole && (
            <Button
              aria-label="edit"
              variant="outlined"
              color="warning"
              startIcon={<EditIcon />}
              disabled={isLoading}
              onClick={() => openEditMode()}
            >
              {t('users-and-roles.roles.details.edit-btn')}
            </Button>
          )}

          {isEditMode && (
            <>
              <Button
                variant="contained"
                onClick={() => onUpdateRole()}
                disabled={isLoading || checkSubmitDisabling()}
              >
                {t('users-and-roles.roles.details.update-btn')}
              </Button>
              <Button variant="outlined" onClick={() => closeEditMode()} disabled={isLoading}>
                {t('users-and-roles.roles.details.cancel-btn')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <InfoField
            name={t('users-and-roles.roles.details.fields.name')}
            value={role?.role.name}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'name',
              value: fields?.name ?? '',
              onChange: fieldsHandler,
              componentType: 'input',
              mitTextAreaRows: 3,
            }}
            isLoading={isLoading}
            withEmptyValue
          />

          <InfoField
            name={t('users-and-roles.roles.details.fields.comment')}
            value={role?.role.comment}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'comment',
              value: fields?.comment ?? '',
              onChange: fieldsHandler,
              componentType: 'textarea',
              mitTextAreaRows: 3,
            }}
            breakSpaces
            isLoading={isLoading}
            withEmptyValue
          />

          <InfoField
            name={t('users-and-roles.roles.details.fields.authorities')}
            value={role?.role.authorities.map((it) => it.name)}
            formattedValue={role?.role.authorities.map((it) => it.name).join(', ')}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'authorities',
              value: fields?.authorities ?? [],
              onChange: fieldsHandler,
              componentType: 'multipleSelect',
              selectOptions: authoritiesSelectOptions,
            }}
            isLoading={isLoading}
            withEmptyValue
          />
        </Paper>
      </Grid>
    </>
  );
};
